import {
  IoPeopleOutline,
  IoCheckmarkCircle,
  IoCloseCircle,
  IoBedOutline,
  IoRestaurantOutline,
  IoPulseOutline,
  IoMailOutline,
  IoGlobeOutline,
  IoLogoInstagram,
  IoLogoTwitter,
  IoLogoLinkedin,
  IoLogoFacebook,
  IoSendOutline,
  IoInformationCircleOutline,
} from "react-icons/io5";
import { LivePal } from "../../types/Pal";
import useSystemTranslation from "../../hooks/useSystemTranslation";
import GearHeadings from "../../ui/gearheadings";

interface LivePalDetailsProps {
  pal: LivePal;
}

const getContactIcon = (type: string) => {
  switch (type.toLowerCase()) {
    case "email":
      return <IoMailOutline className="text-secondary" />;
    case "website":
      return <IoGlobeOutline className="text-secondary" />;
    case "instagram":
      return <IoLogoInstagram className="text-secondary" />;
    case "x":
    case "twitter":
      return <IoLogoTwitter className="text-secondary" />;
    case "linkedin":
      return <IoLogoLinkedin className="text-secondary" />;
    case "facebook":
      return <IoLogoFacebook className="text-secondary" />;
    case "telegram":
      return <IoSendOutline className="text-secondary" />;
    default:
      return <IoInformationCircleOutline className="text-secondary" />;
  }
};

const getContactHref = (type: string, value: string) => {
  switch (type.toLowerCase()) {
    case "email":
      return `mailto:${value}`;
    case "website":
      return value.startsWith("http") ? value : `https://${value}`;
    case "instagram":
      return `https://instagram.com/${value.replace("@", "")}`;
    case "x":
    case "twitter":
      return `https://twitter.com/${value.replace("@", "")}`;
    case "facebook":
      return value.startsWith("http") ? value : `https://facebook.com/${value}`;
    case "linkedin":
      return value.startsWith("http")
        ? value
        : `https://linkedin.com/in/${value}`;
    case "telegram":
      return `https://t.me/${value.replace("@", "")}`;
    default:
      return value.startsWith("http") ? value : "#";
  }
};

const LivePalDetails = ({ pal }: LivePalDetailsProps) => {
  const { translateSystem, t } = useSystemTranslation();

  return (
    <div className="space-y-8">
      {/* Services & General Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="flex items-center gap-4 p-4 bg-white/5 border border-white/5 rounded-xl">
          <div className="p-2.5 bg-secondary/20 rounded-lg">
            <IoPeopleOutline size={22} className="text-secondary" />
          </div>
          <div>
            <p className="text-[10px] font-bold text-white/30 uppercase tracking-widest leading-none mb-1">
              {t("pal.capacity")}
            </p>
            <p className="text-lg font-bold text-white">
              {pal.capacity || 0}{" "}
              <span className="text-sm font-medium text-white/60">
                {t("pal.people")}
              </span>
            </p>
          </div>
        </div>

        <div className="flex items-center gap-4 p-4 bg-white/5 border border-white/5 rounded-xl">
          <div className="p-2.5 bg-primary/20 rounded-lg">
            <IoPulseOutline size={22} className="text-primary" />
          </div>
          <div>
            <p className="text-[10px] font-bold text-white/30 uppercase tracking-widest leading-none mb-1">
              {t("filters.venueType")}
            </p>
            <p className="text-lg font-bold text-white capitalize">
              {pal.venueType}
            </p>
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-3">
        <div
          className={`flex items-center gap-2 px-4 py-1.5 rounded-full border transition-colors ${
            pal.accommodation
              ? "bg-green-500/10 border-green-500/20 text-green-400"
              : "bg-white/5 border-white/5 text-white/30"
          }`}
        >
          <IoBedOutline size={16} />
          <span className="text-[10px] font-bold uppercase tracking-tight">
            {t("filters.accommodation")}
          </span>
          {pal.accommodation ? (
            <IoCheckmarkCircle className="ml-1" size={14} />
          ) : (
            <IoCloseCircle className="ml-1" size={14} />
          )}
        </div>
        <div
          className={`flex items-center gap-2 px-4 py-1.5 rounded-full border transition-colors ${
            pal.meals
              ? "bg-green-500/10 border-green-500/20 text-green-400"
              : "bg-white/5 border-white/5 text-white/30"
          }`}
        >
          <IoRestaurantOutline size={16} />
          <span className="text-[10px] font-bold uppercase tracking-tight">
            {t("filters.meals")}
          </span>
          {pal.meals ? (
            <IoCheckmarkCircle className="ml-1" size={14} />
          ) : (
            <IoCloseCircle className="ml-1" size={14} />
          )}
        </div>
      </div>
      {/* Equipment Sections */}
      <section>
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-secondary/20 rounded-lg">
            <IoPulseOutline size={20} className="text-secondary" />
          </div>
          <GearHeadings
            variant="h4"
            className="text-white uppercase tracking-widest text-xs"
          >
            {t("forms.availableEquipment")}
          </GearHeadings>
        </div>

        <div className="space-y-4">
          {pal?.systems?.map((system) => (
            <div
              key={system.category + system.subCategory}
              className="bg-white/5 border border-white/5 rounded-2xl p-6"
            >
              <h4 className="text-xs font-bold text-secondary uppercase tracking-[0.15em] mb-4 flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-secondary" />
                {translateSystem(system.category as string)}
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 pl-2">
                {system.items.map((item) => (
                  <div key={item} className="flex items-center gap-3 group">
                    <div className="shrink-0 w-4 h-4 rounded-full bg-green-500/10 flex items-center justify-center">
                      <IoCheckmarkCircle size={12} className="text-green-500" />
                    </div>
                    <span className="text-sm text-white/70 group-hover:text-white transition-colors">
                      {translateSystem(item as string)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
      {/* Contacts section consistent with GearPalDetails */}
      {pal.contacts && pal.contacts.length > 0 && (
        <section className="bg-black/20 p-5 rounded-2xl border border-white/5">
          <h3 className="text-[10px] font-bold text-white/40 uppercase tracking-[0.2em] mb-4">
            {t("forms.contactInformation", "Informazioni di Contatto")}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {pal.contacts.map((contact, index) => (
              <a
                key={index}
                href={getContactHref(contact.type, contact.value)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 bg-white/5 rounded-xl border border-white/5 hover:bg-white/10 hover:border-white/10 transition-all group"
              >
                <div className="p-2 bg-white/5 rounded-lg group-hover:bg-secondary/10 transition-colors">
                  {getContactIcon(contact.type)}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[9px] font-bold text-white/30 uppercase tracking-tight leading-none mb-1">
                    {contact.type}
                  </p>
                  <p className="text-sm font-medium text-white/90 truncate group-hover:text-secondary transition-colors">
                    {contact.value}
                  </p>
                </div>
              </a>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default LivePalDetails;
